// Rules behind the scraper scenario editor and its preview runs, kept out of the
// components so they can be tested (src/i18n.js awaits at the top level, so
// nothing that imports it loads under jest). Messages are returned as codes; the
// components translate them.

import { scraperActionKeys } from './scraperSettings';

/**
 * What the API accepts as a scenario name, checked here too so a bad one is
 * caught as it is typed rather than on Save. Lower case and no spaces, because
 * gateways reference scenarios by name in hand-written JSON.
 */
export const SCENARIO_NAME_PATTERN = /^[a-z0-9][a-z0-9._-]{0,99}$/;

export const isValidScenarioName = (name) => SCENARIO_NAME_PATTERN.test(name || '');

/**
 * Editor text read as a JSON object: `{ value }`, or `{ error: 'invalidJson' |
 * 'notObject' }`. Blank text is the empty object — a template or run input with
 * nothing in it is a normal thing to have.
 */
export const parseJsonObjectText = (text) => {
    if (text === null || text === undefined || String(text).trim() === '') return { value: {} };
    let value;
    try {
        value = JSON.parse(text);
    } catch {
        return { error: 'invalidJson' };
    }
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return { error: 'notObject' };
    }
    return { value };
};

/** A JSON object as the text the editors show. */
export const toJsonText = (value) => JSON.stringify(value ?? {}, null, 2);

/**
 * The keys core adds to the input on every call of each action, over the
 * template and the settings row's stored input — with stand-in values, so a
 * preview run can be given the same shape. Mirrors x-backend's xscraper drivers
 * (XScraperPayinGateway, XScraperPayoutGateway); amounts are whole numbers in minor
 * units there, uuids and the merchant's transaction id are text, and a payout's
 * receiverInfo is the merchant's destination, a flat object of strings.
 */
export const ACTION_CALL_INPUT = {
    payin_init: {
        payinUuid: '00000000-0000-4000-8000-000000000003',
        amount: 1000,
        walletUuid: '00000000-0000-4000-8000-000000000001',
        merchantTransactionId: 'preview-1'
    },
    payout_make: {
        payoutUuid: '00000000-0000-4000-8000-000000000004',
        amount: 1000,
        walletUuid: '00000000-0000-4000-8000-000000000001',
        merchantTransactionId: 'preview-1',
        receiverInfo: { card: '4111111111111111' }
    }
};

/** The actions core calls, in the order the settings document them. */
export const CORE_ACTIONS = Object.keys(ACTION_CALL_INPUT);

/** The action core calls on an xscraper settings row of each kind. */
export const KIND_ACTIONS = { payin: 'payin_init', payout: 'payout_make' };

/**
 * The actions a preview can borrow from an xscraper settings row of `kind`: the
 * one core calls on that kind, then any other action its settings hold — an extra
 * action can carry an input worth testing against, even though core never calls
 * it. Without a kind, every core action.
 */
export const previewActions = (settings, kind) => {
    const core = KIND_ACTIONS[kind] ? [KIND_ACTIONS[kind]] : CORE_ACTIONS;
    return [...core, ...scraperActionKeys(settings).filter((key) => !core.includes(key))];
};

/**
 * Run-input text with `action`'s per-call keys added where they are missing —
 * what was typed is never overwritten. Null when the text is not a JSON object,
 * or the action adds no keys: there is nothing sensible to merge into.
 */
export const withExampleInput = (text, action) => {
    const example = ACTION_CALL_INPUT[action];
    if (!example) return null;
    const parsed = parseJsonObjectText(text);
    if (parsed.error) return null;
    return toJsonText({ ...example, ...parsed.value });
};

const IMAGE_TYPES = { jpg: 'jpeg', jpeg: 'jpeg', png: 'png', gif: 'gif', webp: 'webp', svg: 'svg+xml' };

const base64ToUtf8 = (data) => {
    const binary = atob(data);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
};

/**
 * A run's result as something to show:
 *
 *     { kind: 'image', src }                        an <img> source
 *     { kind: 'text', text, json }                  json: the parsed value when the
 *                                                   text is a JSON object or array
 *     { kind: 'empty' }
 *
 * `result` is `{ type: 'text' | 'image', extension, encoding: 'utf-8' | 'base64', data }`.
 */
export const decodeResult = (result) => {
    if (!result || result.data === null || result.data === undefined) return { kind: 'empty' };
    const data = String(result.data);
    const base64 = result.encoding === 'base64';

    if (result.type === 'image') {
        const subtype = IMAGE_TYPES[String(result.extension || 'png').toLowerCase()] || 'png';
        return {
            kind: 'image',
            src: base64
                ? `data:image/${subtype};base64,${data}`
                : `data:image/${subtype};charset=utf-8,${encodeURIComponent(data)}`
        };
    }

    let text = data;
    if (base64) {
        try {
            text = base64ToUtf8(data);
        } catch {
            // Not valid base64 after all: shown as it came.
        }
    }
    let json = null;
    try {
        const parsed = JSON.parse(text);
        if (parsed !== null && typeof parsed === 'object') json = parsed;
    } catch {
        // Plain text.
    }
    return { kind: 'text', text, json };
};

/**
 * Whether core would accept this result from `action`, by the checks its drivers
 * make: every action must return text; payin_init a JSON object with a non-blank
 * `reference`, the bank's id its callback is later matched on; payout_make a JSON
 * object, whose `reference`, the bank's transfer id, is optional.
 *
 * `{ ok: true }`, `{ ok: false, code }`, or null when there is no action or no
 * rule for it.
 */
export const checkResultForAction = (action, decoded) => {
    if (!CORE_ACTIONS.includes(action) || !decoded) return null;
    if (decoded.kind !== 'text') return { ok: false, code: 'notText' };

    if (action === 'payout_make') {
        const { json } = decoded;
        return json && !Array.isArray(json) ? { ok: true } : { ok: false, code: 'payoutNotObject' };
    }
    if (action === 'payin_init') {
        const { json } = decoded;
        if (!json || Array.isArray(json)) return { ok: false, code: 'payinNotObject' };
        const reference = json.reference;
        return reference !== null && reference !== undefined && String(reference).trim() !== ''
            ? { ok: true }
            : { ok: false, code: 'payinNoReference' };
    }
    return { ok: true };
};

/**
 * Where a pointer on the live view lands on the remote page, as fractions (0–1) of
 * the frame — what x-scraper's input takes, so no viewport size is needed here — or
 * null when it is in the letterbox around the frame. `rect` is the <img>'s box,
 * inside which the frame is drawn with object-fit: contain.
 */
export const toFrameFraction = ({ clientX, clientY }, rect, frameWidth, frameHeight) => {
    if (!rect || !frameWidth || !frameHeight || !rect.width || !rect.height) return null;
    const scale = Math.min(rect.width / frameWidth, rect.height / frameHeight);
    const drawnWidth = frameWidth * scale;
    const drawnHeight = frameHeight * scale;
    const x = (clientX - rect.left - (rect.width - drawnWidth) / 2) / drawnWidth;
    const y = (clientY - rect.top - (rect.height - drawnHeight) / 2) / drawnHeight;
    if (!(x >= 0 && x <= 1 && y >= 0 && y <= 1)) return null;
    return { x, y };
};

/** The keys the live view sends to the page by name, as x-scraper takes them. */
export const LIVE_VIEW_KEYS = [
    'Enter', 'Tab', 'Escape', 'Backspace', 'Delete',
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'
];

/**
 * What a key pressed on the live view sends: `{ text }` for a printable character,
 * `{ key }` for one of LIVE_VIEW_KEYS, or null for anything else — any key with
 * Ctrl, ⌘ or Alt included, so the cabinet's shortcuts stay the cabinet's.
 */
export const liveViewKey = ({ key, ctrlKey, metaKey, altKey }) => {
    if (ctrlKey || metaKey || altKey || typeof key !== 'string') return null;
    if (key.length === 1) return { text: key };
    return LIVE_VIEW_KEYS.includes(key) ? { key } : null;
};

/**
 * Whether a recorded line replaces the one recorded just before it rather than
 * going below it. Typing is recorded as the field's whole value, again after each
 * burst of keys, so the newest fill of the same field is the one that counts.
 */
export const replacesLastRecorded = (last, recorded) =>
    Boolean(
        last &&
        recorded &&
        last.action === 'fill' &&
        recorded.action === 'fill' &&
        recorded.selector &&
        recorded.selector === last.selector
    );

/**
 * The proxy a preview was told to use, as the request sends it: a list to pick from
 * (`{ kind: 'list', id }`), one proxy (`{ kind: 'proxy', id }`), or nothing, which
 * leaves it to the settings row's list, or none without a row.
 */
export const proxyChoiceBody = (choice) => {
    if (!choice || !Number.isInteger(choice.id)) return {};
    if (choice.kind === 'list') return { proxyListId: choice.id };
    if (choice.kind === 'proxy') return { proxyId: choice.id };
    return {};
};

/**
 * The options of the preview's proxy picker, lists first: from the proxy lists'
 * light rows (`{ id, name, activeCount, failedCount }`) and the proxies' rows
 * (`{ id, proxy, status, proxyList }`, the proxy already without credentials).
 */
export const proxyOptions = (lists = [], proxies = []) => [
    ...lists.map((list) => ({
        kind: 'list',
        id: list.id,
        label: list.name || `#${list.id}`,
        activeCount: list.activeCount ?? 0,
        failedCount: list.failedCount ?? 0
    })),
    ...proxies.map((proxy) => ({
        kind: 'proxy',
        id: proxy.id,
        label: proxy.proxy || `#${proxy.id}`,
        status: proxy.status,
        listName: proxy.proxyList?.name || ''
    }))
];

/** Whether a remembered choice is still one of `options`. */
export const sameProxyChoice = (a, b) => Boolean(a && b && a.kind === b.kind && a.id === b.id);
